// src/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom'; // 引入 useNavigate
import { Container, Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider, Button, CssBaseline } from '@mui/material'; // Import Material UI components
import AccountCircle from '@mui/icons-material/AccountCircle'; // Import AccountCircle Icon

const ProfilePage = () => {
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate(); // 初始化 useNavigate

    useEffect(() => {
        const fetchUserProfile = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('token'); // 从 localStorage 获取 token (假设登录成功后存储了 token)
                if (!token) {
                    // 如果没有 token，表示未登录，重定向到登录页
                    navigate('/login');
                    return;
                }

                const response = await axios.get('/users/profile', { //  假设后端用户资料接口是 /users/profile
                    headers: {
                        Authorization: `Bearer ${token}`, //  在请求头中携带 token 进行身份验证 (假设使用 JWT)
                    },
                });
                setUserProfile(response.data);
                setLoading(false);
            } catch (error) {
                console.error('获取用户资料失败:', error.response?.data?.message || '获取用户资料失败');
                setError(error.response?.data?.message || '获取用户资料失败');
                setLoading(false);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    // 如果是 401 或 403 错误，表示 token 无效或未授权，重定向到登录页
                    alert('您的登录状态已过期，请重新登录');
                    navigate('/login');
                }
            }
        };

        fetchUserProfile();
    }, [navigate]); //  添加 navigate 到依赖数组

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}> {/* Centered loading indicator */}
            <Typography variant="h6">加载用户资料中...</Typography>
        </Box>;
    }

    if (error) {
        return <Container maxWidth="md" sx={{ mt: 4 }}> {/* Container for error message */}
            <Typography color="error" variant="body1">获取用户资料失败: {error}</Typography>
        </Container>;
    }

    if (!userProfile) {
        return <Container maxWidth="md" sx={{ mt: 4 }}> {/* Container for no profile message */}
            <Typography variant="body1">无法加载用户资料。</Typography>
        </Container>;
    }

    return (
        <Container component="main" maxWidth="md" sx={{ mt: 4 }}> {/* Main container with maxWidth and marginTop */}
            <CssBaseline /> {/* Optional: CssBaseline for consistent styling */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}> {/* Flex container for centering content */}
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main', width: 56, height: 56 }}> {/* Avatar component */}
                    <AccountCircle sx={{ fontSize: 40 }} /> {/* AccountCircle Icon inside Avatar */}
                </Avatar>
                <Typography component="h1" variant="h5" sx={{ mb: 2 }}> {/* Title */}
                    个人资料
                </Typography>

                <List sx={{ width: '100%', maxWidth: 500, bgcolor: 'background.paper' }}> {/* List component for profile details */}
                    <ListItem> {/* List Item for Username */}
                        <ListItemAvatar>
                            <Avatar><AccountCircle /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="用户名" secondary={userProfile.username || '未设置'} /> {/* Display username or '未设置' */}
                    </ListItem>
                    <Divider component="li" /> {/* Divider */}
                    <ListItem> {/* List Item for Email */}
                        <ListItemAvatar>
                            <Avatar><AccountCircle /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="邮箱" secondary={userProfile.email} /> {/* Display email */}
                    </ListItem>
                    <Divider component="li" /> {/* Divider */}
                    <ListItem> {/* List Item for Role */}
                        <ListItemAvatar>
                            <Avatar><AccountCircle /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="角色" secondary={userProfile.role || 'Free'} /> {/* Display role or 'Free' */}
                    </ListItem>
                    <Divider component="li" /> {/* Divider */}
                    <ListItem> {/* List Item for Payment Status */}
                        <ListItemAvatar>
                            <Avatar><AccountCircle /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="付费状态" secondary={userProfile.isPaid ? '已付费' : '免费'} /> {/* Display payment status */}
                    </ListItem>
                </List>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}> {/* Box for buttons, Flex layout for horizontal arrangement */}
                    <Button variant="contained" component={Link} to="/subscription-management"> {/* Button to Subscription Management */}
                        订阅管理
                    </Button>
                    <Button variant="outlined" component={Link} to="/"> {/* Button to Homepage */}
                        返回首页
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default ProfilePage;